<template>
	<slot v-if="isDisabled"></slot>
	<span
		v-else
		ref="rootRef"
		:class="wrapperClasses"
		@mouseenter="onMouseEnter"
		@mouseleave="onMouseLeave"
		@click="onClickTrigger"
		@focusin="onFocusIn"
		@focusout="onFocusOut"
	>
		<slot></slot>
		<Tooltip
			:anchor-el="rootRef"
			:open="isOpen"
			:text="props.text"
			:html="props.html"
			:color="props.color"
			:placement="props.placement"
			:multiline="isMultiline"
			:max-width="props.maxWidth"
			:show-arrow="props.showArrow"
			:actions="props.actions"
			:interactive="props.interactive"
			@action-click="onActionClick"
			@hover-change="onTooltipHoverChange"
		>
			<template v-if="$slots.content" #content>
				<slot name="content"></slot>
			</template>
		</Tooltip>
	</span>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useBemm } from 'bemm';
import type { TooltipAction, TooltipOptions } from './Tooltip.model';
import Tooltip from './Tooltip.vue';

defineOptions({
	name: 'ArTooltipWrapper',
});

const props = withDefaults(
	defineProps<
		TooltipOptions & {
			modelValue?: boolean;
			stretch?: boolean;
		}
	>(),
	{
		text: '',
		html: '',
		color: 'default',
		placement: 'top',
		trigger: 'hover',
		interactive: false,
		multiline: false,
		maxWidth: 320,
		openDelay: 120,
		closeDelay: 80,
		showArrow: true,
		actions: () => [],
		disabled: false,
		modelValue: undefined,
		stretch: false,
	}
);

const emit = defineEmits<{
	(e: 'update:modelValue', value: boolean): void;
	(e: 'open'): void;
	(e: 'close'): void;
	(e: 'action-click', action: TooltipAction): void;
}>();

const bemm = useBemm('ar-tooltip', {
	return: 'string',
	includeBaseClass: true,
});

const rootRef = ref<HTMLElement | null>(null);
const internalOpen = ref(false);
const openTimer = ref<number | null>(null);
const closeTimer = ref<number | null>(null);
const triggerHovered = ref(false);
const tooltipHovered = ref(false);

const isControlled = computed(() => typeof props.modelValue === 'boolean');
const isOpen = computed(() =>
	isControlled.value ? Boolean(props.modelValue) : internalOpen.value
);
const isDisabled = computed(() => props.disabled);
const isMultiline = computed(
	() => props.multiline || props.text.includes('\n')
);
const opensOnHover = computed(
	() => props.trigger === 'hover' || props.trigger === 'hover-click'
);
const opensOnClick = computed(
	() => props.trigger === 'click' || props.trigger === 'hover-click'
);

const clearTimers = () => {
	if (openTimer.value !== null) {
		window.clearTimeout(openTimer.value);
		openTimer.value = null;
	}
	if (closeTimer.value !== null) {
		window.clearTimeout(closeTimer.value);
		closeTimer.value = null;
	}
};

const setOpen = (next: boolean) => {
	if (props.disabled && next) return;
	if (!isControlled.value) {
		internalOpen.value = next;
	}
	emit('update:modelValue', next);
	if (next) {
		emit('open');
		return;
	}
	emit('close');
};

const open = () => {
	clearTimers();
	openTimer.value = window.setTimeout(() => setOpen(true), props.openDelay);
};

const close = () => {
	clearTimers();
	closeTimer.value = window.setTimeout(() => setOpen(false), props.closeDelay);
};

const onMouseEnter = () => {
	triggerHovered.value = true;
	if (!opensOnHover.value) return;
	open();
};

const onMouseLeave = () => {
	triggerHovered.value = false;
	if (!opensOnHover.value) return;
	if (props.interactive && tooltipHovered.value) return;
	close();
};

const onFocusIn = () => {
	triggerHovered.value = true;
	if (props.trigger === 'manual') return;
	open();
};

const onFocusOut = () => {
	triggerHovered.value = false;
	if (props.trigger === 'manual') return;
	if (props.interactive && tooltipHovered.value) return;
	close();
};

const onClickTrigger = () => {
	if (!opensOnClick.value) return;
	// hover-click is open-only; hover drives closing.
	if (props.trigger === 'hover-click') {
		setOpen(true);
		return;
	}
	setOpen(!isOpen.value);
};

const onDocumentClick = (event: MouseEvent) => {
	if (!opensOnClick.value || !isOpen.value) return;
	const root = rootRef.value;
	if (root && !root.contains(event.target as Node)) {
		setOpen(false);
	}
};

const onActionClick = (action: TooltipAction) => {
	emit('action-click', action);
	if (props.trigger !== 'manual') {
		setOpen(false);
	}
};

const onTooltipHoverChange = (hovered: boolean) => {
	if (!props.interactive) {
		return;
	}

	tooltipHovered.value = hovered;
	if (hovered) {
		clearTimers();
		setOpen(true);
		return;
	}

	if (!triggerHovered.value) {
		close();
	}
};

watch(
	() => props.disabled,
	(disabled) => {
		if (disabled) {
			clearTimers();
			setOpen(false);
		}
	}
);

const wrapperClasses = computed(() =>
	bemm('', {
		open: isOpen.value,
		stretch: props.stretch,
	})
);

onMounted(() => {
	document.addEventListener('click', onDocumentClick);
});

onBeforeUnmount(() => {
	document.removeEventListener('click', onDocumentClick);
	clearTimers();
});
</script>

<style lang="scss">
.ar-tooltip {
	position: relative;
	display: inline-flex;

	&--stretch {
		display: flex;
		width: 100%;
	}
}
</style>
